import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';
import { change } from 'redux-form';
import { useDispatch } from 'react-redux';
import styles from './dnd.module.scss';

type Registry = Map<string, any>;

const Ctx = createContext<{
    register: (droppableId: string, fields: any) => void;
    unregister: (droppableId: string) => void;
} | null>(null);

export const useDndRegistry = () => {
    const v = useContext(Ctx);
    if (!v) throw new Error('useDndRegistry must be used within DndProvider');
    return v;
};

export const useRegisterDroppable = (droppableId: string, fields: any) => {
    const { register, unregister } = useDndRegistry();

    useEffect(() => {
        register(droppableId, fields);
        return () => unregister(droppableId);
    }, [droppableId, fields, register, unregister]);
};


export const DndProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const registry = useRef<Registry>(new Map());
    const [isProcessing, setIsProcessing] = useState(false);
    const pendingDrop = useRef<DropResult | null>(null);
    const dispatch = useDispatch();

    const updateSortOrder = useCallback((fields: any, count: number) => {
        for (let i = 0; i < count; i++) {
            dispatch(change('program-book', `${fields.name}[${i}].sortOrder`, i));
        }
    }, [dispatch]);

    const register = useCallback((droppableId: string, fields: any) => {
        registry.current.set(droppableId, fields);
    }, []);
    const unregister = useCallback((droppableId: string) => {
        registry.current.delete(droppableId);
    }, []);

    const onDragEnd = useCallback((result: DropResult) => {
        const { source, destination, type } = result;
        if (!destination) return;
        if (source.droppableId === destination.droppableId && source.index === destination.index) return;

        const sourceFields = registry.current.get(source.droppableId);
        const destFields = registry.current.get(destination.droppableId);
        if (!sourceFields || !destFields) return;

        const same = source.droppableId === destination.droppableId;
        if (!same && type !== 'targets' && type !== 'shortterms' && type !== 'longterms' && type !== 'domains') return;

        pendingDrop.current = result;
        setIsProcessing(true);
    }, []);

    // Once the loader has rendered — run the heavy move
    useEffect(() => {
        if (!isProcessing || !pendingDrop.current) return;

        const result = pendingDrop.current;
        pendingDrop.current = null;

        const { source, destination } = result;
        if (!destination) {
            setIsProcessing(false);
            return;
        }

        // A double rAF guarantees the loader has painted on screen
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const sourceFields = registry.current.get(source.droppableId);
                const destFields = registry.current.get(destination.droppableId);

                if (sourceFields && destFields) {
                    const same = source.droppableId === destination.droppableId;
                    const srcLen = sourceFields.length;
                    const dstLen = destFields.length;

                    if (same) {
                        sourceFields.move(source.index, destination.index);
                        updateSortOrder(sourceFields, srcLen);
                    } else {
                        const item = sourceFields.get(source.index);
                        sourceFields.remove(source.index);
                        destFields.insert(destination.index, item);
                        updateSortOrder(sourceFields, srcLen - 1);
                        updateSortOrder(destFields, dstLen + 1);
                    }
                }

                setIsProcessing(false);
            });
        });
    }, [isProcessing]);

    return (
        <Ctx.Provider value={{ register, unregister }}>
            <DragDropContext onDragEnd={onDragEnd}>
                {children}
                {isProcessing && (
                    <div className={styles.overlay}>
                        <div className={styles.spinner} />
                        <span className={styles.label}>Updating...</span>
                    </div>
                )}
            </DragDropContext>
        </Ctx.Provider>
    );
};
